/**
 * Resolve whether an image URL is loadable.
 *
 * On web (and the extension popup) we probe via an actual `<img>` element load
 * rather than `fetch`. A plain image load is NOT subject to the CORS policy
 * (only reading a cross-origin image's pixels is) — `fetch`, even a HEAD, IS.
 * So a `fetch` probe falsely reports cross-origin icons served without an
 * `Access-Control-Allow-Origin` header (e.g. libertyswap.finance/icons/token/*)
 * as missing, even though the browser would happily render them in an <img>.
 *
 * On native (React Native) there is no DOM `Image` constructor, but RN's fetch
 * has no CORS restriction, so the original HEAD probe is used there.
 *
 * {@link https://stackoverflow.com/a/56196999/1333836}
 */
const PROBE_TIMEOUT_MS = 8000

export const checkIfImageExists = (uri?: string): Promise<boolean> => {
  if (!uri) return Promise.resolve(false)

  if (typeof document !== 'undefined' && typeof Image !== 'undefined') {
    return new Promise<boolean>((resolve) => {
      const img = new Image()
      let settled = false
      let timer: ReturnType<typeof setTimeout>
      const done = (ok: boolean) => {
        if (settled) return
        settled = true
        clearTimeout(timer)
        // Free the request/handlers promptly.
        img.onload = null
        img.onerror = null
        resolve(ok)
      }
      // If it loaded at all, it exists. Don't gate on naturalWidth — some SVGs
      // have no intrinsic size and would report 0 despite rendering fine.
      img.onload = () => done(true)
      img.onerror = () => done(false)
      timer = setTimeout(() => done(false), PROBE_TIMEOUT_MS)
      img.src = uri
    })
  }

  return fetch(uri, {
    // Only retrieves headers, which is enough to check the image existence.
    method: 'HEAD'
  })
    .then((res) => res.ok)
    .catch(() => false)
}
